import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Bell, Search, Link2, Link2Off, CircleUser, ShieldAlert, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import Sidebar from "./Sidebar";
import { useAuth } from "@/context/AuthContext";
import api, { arr } from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";

export default function Topbar() {
  const { user, client, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [googleConnected, setGoogleConnected] = useState(null);

  const isClient = user?.role !== "super_admin";

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotifs({ notifications: arr(data?.notifications), unread: data?.unread || 0 })).catch(() => {});
    if (isClient) api.get("/google/status").then(({ data }) => setGoogleConnected(data.connected)).catch(() => {});
  }, [isClient]);

  const markAllRead = async () => {
    await api.put("/notifications/read-all");
    setNotifs((n) => ({ ...n, unread: 0, notifications: arr(n.notifications).map((x) => ({ ...x, read: true })) }));
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const profilePath = isClient ? "/app/settings" : "/admin/settings";

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/70 px-4 backdrop-blur-xl backdrop-saturate-150 sm:px-6">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 p-0">
          <Sidebar />
        </SheetContent>
      </Sheet>

      <div className="relative hidden flex-1 max-w-md md:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="topbar-search"
          placeholder="Search..."
          className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none transition-all focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200"
        />
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {user?.impersonatedBy && (
          <span className="hidden items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 sm:inline-flex">
            <ShieldAlert className="h-3.5 w-3.5" /> Support Mode
          </span>
        )}

        {isClient && (
          <span
            data-testid="google-status-badge"
            className={`hidden items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold sm:inline-flex ${
              googleConnected ? "border-green-200 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
          >
            {googleConnected ? <Link2 className="h-3.5 w-3.5" /> : <Link2Off className="h-3.5 w-3.5" />}
            {googleConnected ? "Google Connected" : "Google Not Connected"}
          </span>
        )}

        {isClient && client?.status && (
          <span className="hidden sm:inline-flex">
            <StatusBadge status={client.status} />
          </span>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="notification-bell" className="relative flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100">
              <Bell className="h-5 w-5" />
              {notifs.unread > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {notifs.unread > 9 ? "9+" : notifs.unread}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-96 rounded-2xl p-0">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-800">Notifications</p>
                {notifs.unread > 0 && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-600">{notifs.unread} new</span>
                )}
              </div>
              {notifs.unread > 0 && (
                <button onClick={markAllRead} className="text-xs font-medium text-brand-600 hover:underline" data-testid="mark-all-read-btn">
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto scrollbar-thin">
              {notifs.notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-400">You're all caught up 🎉</p>
              ) : (
                notifs.notifications.map((n) => (
                  <NotifItem key={n.id} n={n} onRead={(id) => {
                    api.put(`/notifications/${id}/read`).catch(() => {});
                    setNotifs((prev) => ({
                      ...prev,
                      unread: Math.max(0, prev.unread - (n.read ? 0 : 1)),
                      notifications: prev.notifications.map((x) => x.id === id ? { ...x, read: true } : x),
                    }));
                  }} />
                ))
              )}
            </div>
            {notifs.notifications.length > 0 && isClient && (
              <div className="border-t border-slate-100 px-4 py-3">
                <a href="/app/notifications" className="block text-center text-xs font-medium text-brand-600 hover:underline">
                  View all notifications →
                </a>
              </div>
            )}
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="profile-menu-trigger" className="flex items-center gap-2 rounded-xl py-1 pl-1 pr-2 transition-colors hover:bg-slate-100">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-brand-700 text-xs font-semibold text-white">{initials}</AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium text-slate-700 sm:block">{user?.name?.split(" ")[0]}</span>
              <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-2xl">
            <DropdownMenuLabel>
              <p className="font-semibold text-slate-800">{user?.name}</p>
              <p className="text-xs font-normal capitalize text-slate-400">{user?.role?.replace("_", " ")}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(profilePath)} data-testid="profile-settings-item">
              <CircleUser className="mr-2 h-4 w-4" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600" data-testid="profile-logout-item">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const notifTypeColor = (type) => ({
  bad_review: "border-l-red-400 bg-red-50/40",
  expiry: "border-l-amber-400 bg-amber-50/40",
  report: "border-l-blue-400 bg-blue-50/40",
  info: "border-l-brand-400 bg-brand-50/30",
}[type] || "border-l-slate-200");

function NotifItem({ n, onRead }) {
  return (
    <button
      onClick={() => !n.read && onRead(n.id)}
      className={`w-full border-b border-slate-50 border-l-4 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${
        !n.read ? notifTypeColor(n.type) : "border-l-transparent"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={`text-sm ${!n.read ? "font-semibold text-slate-800" : "font-medium text-slate-600"}`}>{n.title}</p>
        {!n.read && <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />}
      </div>
      {n.message && <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{n.message}</p>}
      <p className="mt-1 text-[10px] text-slate-400">{timeAgo(n.createdAt)}</p>
    </button>
  );
}
